import { useCallback, useState, ReactNode } from 'react';
import { Button, Modal } from 'react-bootstrap';

interface ConfirmationModalProps {
  title: string;
  text?: string;
  htmlText?: ReactNode;
  show?: boolean;
  onHide?: (accepted: boolean) => void;
  cancelVariant?: string;
  confirmVariant?: string;
  cancelText?: string;
  confirmText?: string;
}

export const ConfirmationModal = ({
  title,
  text,
  htmlText,
  show,
  onHide = () => {},
  cancelVariant = 'outline-primary',
  confirmVariant = 'primary',
  cancelText = 'Cancel',
  confirmText = 'Confirm',
}: ConfirmationModalProps) => {
  return (
    <Modal show={show}>
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>{text || htmlText}</Modal.Body>
      <Modal.Footer>
        <Button variant={cancelVariant} onClick={() => onHide(false)}>
          {cancelText}
        </Button>
        <Button variant={confirmVariant} onClick={() => onHide(true)}>
          {confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const useConfirmationModal = () => {
  const [show, setShow] = useState(false);
  const [resolvePromise, setResolvePromise] = useState(() => (accepted: boolean) => {});

  const showConfirmationModal = useCallback(() => {
    return new Promise<boolean>((resolve) => {
      setShow(true);
      setResolvePromise(() => resolve);
    });
  }, []);

  const hideConfirmationModal = useCallback(
    (accepted: boolean) => {
      setShow(false);
      resolvePromise(accepted);
    },
    [resolvePromise]
  );

  // The component to be rendered using the hook's state
  const _ConfirmationModal = useCallback(
    (props: ConfirmationModalProps) => (
      <ConfirmationModal {...props} onHide={hideConfirmationModal} show={show} />
    ),
    [show, hideConfirmationModal]
  );

  return { showConfirmationModal, hideConfirmationModal, ConfirmationModal: _ConfirmationModal };
};
